import { useEffect, useRef, useState } from 'react';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, Label, Textarea } from '../../ui';
import { useAdminApi } from '../../hooks/useAdminApi';

interface Career {
  id: number;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  status: string;
  description: string | null;
  requirements: string | null;
  responsibilities: string | null;
  qualifications: string | null;
  benefits: string | null;
  salary_range: string | null;
  created_at: string;
  updated_at: string;
}

interface CareerForm {
  title: string;
  department: string;
  location: string;
  employment_type: string;
  status: string;
  description: string;
  requirements: string;
  responsibilities: string;
  qualifications: string;
  benefits: string;
  salary_range: string;
}

const EMPTY_FORM: CareerForm = {
  title: '',
  department: '',
  location: '',
  employment_type: 'Full-time',
  status: 'new',
  description: '',
  requirements: '',
  responsibilities: '',
  qualifications: '',
  benefits: '',
  salary_range: '',
};

export default function AdminCareers() {
  const [careers, setCareers] = useState<Career[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const api = useAdminApi();

  const fetchCareers = () => {
    api.get('/api/admin/careers')
      .then((res) => setCareers(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const initRef = useRef(false);
  useEffect(() => { if (!initRef.current) { initRef.current = true; fetchCareers(); } }, [api]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (career: Career) => {
    setForm({
      title: career.title,
      department: career.department,
      location: career.location,
      employment_type: career.employment_type,
      status: career.status,
      description: career.description || '',
      requirements: career.requirements || '',
      responsibilities: career.responsibilities || '',
      qualifications: career.qualifications || '',
      benefits: career.benefits || '',
      salary_range: career.salary_range || '',
    });
    setEditingId(career.id);
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingId) {
        await api.put(`/api/admin/careers/${editingId}`, form);
      } else {
        await api.post('/api/admin/careers', form);
      }
      resetForm();
      fetchCareers();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save career posting');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this career posting?')) return;
    try {
      await api.del(`/api/admin/careers/${id}`);
      fetchCareers();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleToggleStatus = async (career: Career) => {
    const newStatus = career.status === 'new' ? 'closed' : 'new';
    try {
      await api.put(`/api/admin/careers/${career.id}`, { status: newStatus });
      fetchCareers();
    } catch (err) {
      console.error('Status toggle failed:', err);
    }
  };

  const filtered = careers.filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return c.title.toLowerCase().includes(q) ||
      c.department.toLowerCase().includes(q) ||
      c.location.toLowerCase().includes(q);
  });

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading careers...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Career Postings ({careers.length})</h2>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs"
          />
          <Button onClick={() => { resetForm(); setShowForm(true); }}>
            + New Posting
          </Button>
        </div>
      </div>

      {/* Career Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Career Posting' : 'New Career Posting'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => updateField('title', e.target.value)} required />
                </div>
                <div className="space-y-1">
                  <Label>Department *</Label>
                  <Input value={form.department} onChange={(e) => updateField('department', e.target.value)} required />
                </div>
                <div className="space-y-1">
                  <Label>Location *</Label>
                  <Input value={form.location} onChange={(e) => updateField('location', e.target.value)} required />
                </div>
                <div className="space-y-1">
                  <Label>Employment Type *</Label>
                  <select
                    value={form.employment_type}
                    onChange={(e) => updateField('employment_type', e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                    required
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                    <option value="Remote">Remote</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Salary Range</Label>
                  <Input value={form.salary_range} onChange={(e) => updateField('salary_range', e.target.value)} placeholder="e.g. $80,000 - $120,000" />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <select
                    value={form.status}
                    onChange={(e) => updateField('status', e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm"
                  >
                    <option value="new">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} rows={3} />
              </div>
              <div className="space-y-1">
                <Label>Requirements</Label>
                <Textarea value={form.requirements} onChange={(e) => updateField('requirements', e.target.value)} rows={3} />
              </div>
              <div className="space-y-1">
                <Label>Responsibilities</Label>
                <Textarea value={form.responsibilities} onChange={(e) => updateField('responsibilities', e.target.value)} rows={3} />
              </div>
              <div className="space-y-1">
                <Label>Qualifications</Label>
                <Textarea value={form.qualifications} onChange={(e) => updateField('qualifications', e.target.value)} rows={3} />
              </div>
              <div className="space-y-1">
                <Label>Benefits</Label>
                <Textarea value={form.benefits} onChange={(e) => updateField('benefits', e.target.value)} rows={3} />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update Posting' : 'Create Posting'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Careers Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">Title</th>
              <th className="px-3 py-2 text-left font-medium">Department</th>
              <th className="px-3 py-2 text-left font-medium">Location</th>
              <th className="px-3 py-2 text-left font-medium">Type</th>
              <th className="px-3 py-2 text-left font-medium">Salary</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Created</th>
              <th className="px-3 py-2 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b hover:bg-muted/30">
                <td className="px-3 py-2 font-medium">{c.title}</td>
                <td className="px-3 py-2">{c.department}</td>
                <td className="px-3 py-2">{c.location}</td>
                <td className="px-3 py-2">{c.employment_type}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.salary_range || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${c.status.toUpperCase() === 'NEW' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {c.status.toUpperCase() === 'NEW' ? 'Active' : 'Closed'}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {new Date(c.created_at).toLocaleDateString()}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => startEdit(c)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleToggleStatus(c)}>
                      {c.status.toUpperCase() === 'NEW' ? 'Close' : 'Reopen'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(c.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {filter ? 'No careers match your search' : 'No career postings yet'}
          </div>
        )}
      </div>
    </div>
  );
}
